import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuthSession, clearAuthSession } from '../services/api';

const DashboardSidebar = ({ userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authSession = getAuthSession();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login');
  };

  const getNavLinks = () => {
    if (userRole === 'admin') {
      return [
        { icon: '📊', label: 'Dashboard', path: '/admin-dashboard' },
        { icon: '🛍️', label: 'DigiStore Products', path: '/admin/digistore' },
        { icon: '⏳', label: 'Waitlist Enquiries', path: '/admin/waitlist' },
        { icon: '📑', label: 'Worksheets Library', path: '/admin/worksheets' },
        { icon: '📝', label: 'Worksheet Builder', path: '/admin/worksheet-forms' },
        { icon: '💡', label: 'Self-Help Tools', path: '/admin/self-help' },
        { icon: '✓', label: 'Verify Therapists', path: '/admin/verifications' },
        { icon: '👨‍⚕️', label: 'Therapists', path: '/admin/therapists' },
        { icon: '🎖️', label: 'Milestone Badges', path: '/admin/badges' },
        { icon: '🏷️', label: 'Categories', path: '/admin/therapist-categories' },
        { icon: '📚', label: 'Blog Categories', path: '/admin/blog-categories' },
        { icon: '🛋️', label: 'Session Types', path: '/admin/session-types' },
        { icon: '👥', label: 'Manage Users', path: '/admin/users' },
        { icon: '🎉', label: 'Events & Seminars', path: '/admin/events' },
        { icon: '📝', label: 'Blog & Content', path: '/admin/content' },
        { icon: '🚫', label: 'Prohibited Words', path: '/admin/prohibited-words' },
        { icon: '❓', label: 'Manage FAQs', path: '/admin/faqs' },
        { icon: '📈', label: 'Analytics', path: '/admin/analytics' }
      ];
    } else if (userRole === 'physiotherapist') {
      return [
        { icon: '📊', label: 'Dashboard', path: '/physio-dashboard' },
        { icon: '💬', label: 'Community Posts', path: '/therapist/community' },
        { icon: '📑', label: 'Clinical Worksheets', path: '/therapist/worksheets' },
        { icon: '📅', label: 'My Bookings', path: '/therapist/bookings' },
        { icon: '⏰', label: 'Availability', path: '/therapist/availability' },
        { icon: '👤', label: 'My Profile', path: '/therapist/profile' },
        { icon: '✍️', label: 'Write Blog', path: '/therapist/blogs/new' },
        { icon: '🎉', label: 'Create Event', path: '/therapist/events/new' },
        { icon: '📚', label: 'Upload Resource', path: '/therapist/resources/upload' }
      ];
    } else {
      return [
        { icon: '📊', label: 'Dashboard', path: '/client-dashboard' },
        { icon: '👤', label: 'My Profile', path: '/client/profile' },
        { icon: '📑', label: 'My Worksheets', path: '/client/worksheets' },
        { icon: '🔍', label: 'Find Therapist', path: '/therapists' },
        { icon: '📅', label: 'My Bookings', path: '/client/bookings' },
        { icon: '💭', label: 'Self-Help', path: '/self-help' },
        { icon: '👥', label: 'Community Wall', path: '/community' }
      ];
    }
  };

  const navLinks = getNavLinks();
  const userName = authSession?.user?.name || 'User';
  const userEmail = authSession?.user?.email || '';
  const userInitial = userName.charAt(0).toUpperCase();

  const getPortalLabel = () => {
    if (userRole === 'admin') return 'Admin Suite';
    if (userRole === 'physiotherapist') return 'Therapist Hub';
    return 'Client Portal';
  };

  return (
    <>
      {/* Mobile Toggle Button (3-line bar) */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="nexaflow-sidebar-mobile-toggle"
        aria-label="Toggle navigation menu"
      >
        {isMobileOpen ? (
          <span style={{ fontSize: '20px', lineHeight: 1 }}>✕</span>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        )}
      </button>

      {/* Backdrop for mobile */}
      {isMobileOpen && (
        <div
          className="nexaflow-sidebar-backdrop"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* NexaFlow Refined Sidebar */}
      <aside className={`nexaflow-sidebar ${isMobileOpen ? 'mobile-open' : ''}`}>
        {/* Brand Header */}
        <div className="nexaflow-sidebar-brand">
          <div className="brand-icon-wrapper">
            <span className="brand-icon">✦</span>
          </div>
          <div className="brand-text-wrapper">
            <h2 className="brand-title">PsychMitra</h2>
            <span className="brand-subtitle">{getPortalLabel()}</span>
          </div>
          <button
            className="nexaflow-sidebar-close-btn"
            onClick={() => setIsMobileOpen(false)}
            aria-label="Close navigation menu"
          >
            ✕
          </button>
        </div>

        {/* User Mini Card */}
        <div className="nexaflow-sidebar-user">
          <div className="user-avatar-ring">
            <span className="user-avatar-initial">{userInitial}</span>
          </div>
          <div className="user-details">
            <h4 className="user-name" title={userName}>{userName}</h4>
            <span className="user-role-badge">
              <span className="status-dot"></span>
              {userRole === 'physiotherapist' ? 'Practitioner' : userRole === 'admin' ? 'Administrator' : 'Active Client'}
            </span>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="nexaflow-nav-section-title">MAIN NAVIGATION</div>
        <nav className="nexaflow-sidebar-nav">
          {navLinks.map((link, idx) => {
            const isActive = location.pathname === link.path || (link.path !== '/' && location.pathname.startsWith(link.path + '/'));
            return (
              <Link
                key={idx}
                to={link.path}
                onClick={() => setIsMobileOpen(false)}
                className={`nexaflow-nav-link ${isActive ? 'active' : ''}`}
              >
                <span className="nav-icon">{link.icon}</span>
                <span className="nav-label">{link.label}</span>
                {isActive && <span className="nav-active-pill"></span>}
              </Link>
            );
          })}
        </nav>

        {/* Bottom Profile & Actions Section */}
        <div className="nexaflow-sidebar-footer">
          <a href="/" className="footer-action-btn home-link">
            <span className="btn-icon">🏠</span>
            <span>Return to Site</span>
          </a>

          <button onClick={handleLogout} className="footer-action-btn logout-link">
            <span className="btn-icon">🚪</span>
            <span>Secure Logout</span>
          </button>
        </div>
      </aside>

      <style>{`
        /* NexaFlow Sidebar Design Language */
        .nexaflow-sidebar {
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          width: 270px;
          background: #ffffff;
          border-right: 1px solid rgba(122, 96, 202, 0.12);
          box-shadow: 2px 0 24px rgba(122, 96, 202, 0.04);
          display: flex;
          flex-direction: column;
          z-index: 999;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .nexaflow-sidebar-brand {
          padding: 24px 22px 18px 22px;
          display: flex;
          align-items: center;
          gap: 12px;
          border-bottom: 1px solid rgba(122, 96, 202, 0.08);
        }

        .brand-icon-wrapper {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          background: linear-gradient(135deg, #7A60CA 0%, #6347BF 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ffffff;
          font-size: 20px;
          box-shadow: 0 4px 14px rgba(122, 96, 202, 0.35);
          flex-shrink: 0;
        }

        .brand-text-wrapper {
          display: flex;
          flex-direction: column;
        }

        .brand-title {
          margin: 0;
          font-size: 19px;
          font-weight: 800;
          color: #1A162B;
          letter-spacing: -0.5px;
          line-height: 1.15;
        }

        .brand-subtitle {
          font-size: 11px;
          font-weight: 700;
          color: #7A60CA;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          margin-top: 2px;
        }

        /* User Card */
        .nexaflow-sidebar-user {
          margin: 16px 16px 12px 16px;
          padding: 12px 14px;
          background: #F8F7FD;
          border-radius: 14px;
          border: 1px solid rgba(122, 96, 202, 0.08);
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .user-avatar-ring {
          width: 38px;
          height: 38px;
          border-radius: 10px;
          background: linear-gradient(135deg, #7A60CA 0%, #9580D4 100%);
          color: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 15px;
          box-shadow: 0 3px 10px rgba(122, 96, 202, 0.2);
          flex-shrink: 0;
        }

        .user-details {
          overflow: hidden;
          flex: 1;
        }

        .user-name {
          margin: 0;
          font-size: 13.5px;
          font-weight: 700;
          color: #1A162B;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role-badge {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          font-size: 11px;
          color: #64748B;
          font-weight: 600;
          margin-top: 2px;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #10B981;
          box-shadow: 0 0 6px rgba(16, 185, 129, 0.5);
        }

        .nexaflow-nav-section-title {
          padding: 8px 24px 6px 24px;
          font-size: 10.5px;
          font-weight: 800;
          color: #94A3B8;
          letter-spacing: 0.9px;
          text-transform: uppercase;
        }

        /* Nav list */
        .nexaflow-sidebar-nav {
          flex: 1;
          padding: 4px 14px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .nexaflow-sidebar-nav::-webkit-scrollbar {
          width: 5px;
        }
        .nexaflow-sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(122, 96, 202, 0.15);
          border-radius: 10px;
        }

        .nexaflow-nav-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 14px;
          border-radius: 11px;
          text-decoration: none;
          color: #554F68;
          font-size: 13.5px;
          font-weight: 600;
          transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
          position: relative;
        }

        .nexaflow-nav-link:hover {
          background: #F4F1FB;
          color: #7A60CA;
          transform: translateX(3px);
        }

        .nexaflow-nav-link.active {
          background: #7A60CA;
          color: #ffffff;
          font-weight: 700;
          box-shadow: 0 4px 16px rgba(122, 96, 202, 0.3);
        }

        .nexaflow-nav-link.active:hover {
          transform: none;
          color: #ffffff;
        }

        .nav-icon {
          font-size: 17px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
        }

        .nav-label {
          flex: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .nav-active-pill {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #ffffff;
          box-shadow: 0 0 8px rgba(255, 255, 255, 0.8);
        }

        /* Bottom Actions */
        .nexaflow-sidebar-footer {
          padding: 14px 16px 18px 16px;
          border-top: 1px solid rgba(122, 96, 202, 0.08);
          display: flex;
          flex-direction: column;
          gap: 8px;
          background: #FAFAFD;
        }

        .footer-action-btn {
          width: 100%;
          padding: 10px 14px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 600;
          text-decoration: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.2s ease;
          border: 1px solid transparent;
          box-sizing: border-box;
        }

        .footer-action-btn.home-link {
          background: #FFFFFF;
          border-color: rgba(122, 96, 202, 0.15);
          color: #7A60CA;
        }
        .footer-action-btn.home-link:hover {
          background: #F5F1FD;
          border-color: #7A60CA;
          transform: translateY(-1px);
        }

        .footer-action-btn.logout-link {
          background: #FFF5F5;
          border-color: #FED7D7;
          color: #DC2626;
        }
        .footer-action-btn.logout-link:hover {
          background: #FEE2E2;
          border-color: #F87171;
          transform: translateY(-1px);
        }

        .btn-icon {
          font-size: 15px;
        }

        /* Mobile Responsive */
        .nexaflow-sidebar-mobile-toggle {
          display: none;
          position: fixed;
          top: 14px;
          left: 14px;
          z-index: 1001;
          background: #7A60CA;
          color: #ffffff;
          border: none;
          border-radius: 11px;
          width: 40px;
          height: 40px;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 4px 14px rgba(122, 96, 202, 0.4);
          transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
          padding: 0;
        }

        .nexaflow-sidebar-mobile-toggle:hover {
          background: #6347BF;
          transform: scale(1.05);
        }

        .nexaflow-sidebar-mobile-toggle:active {
          transform: scale(0.96);
        }

        .nexaflow-sidebar-backdrop {
          display: none;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(15, 23, 42, 0.5);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 998;
          animation: fadeIn 0.2s ease-out;
        }

        .nexaflow-sidebar-close-btn {
          display: none;
          background: #F4F1FB;
          color: #554F68;
          border: none;
          border-radius: 9px;
          width: 32px;
          height: 32px;
          font-size: 15px;
          font-weight: 700;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          margin-left: auto;
          transition: all 0.2s ease;
        }

        .nexaflow-sidebar-close-btn:hover {
          background: #EDE9FE;
          color: #7A60CA;
        }

        @media (max-width: 1024px) {
          .nexaflow-sidebar {
            width: 285px;
            max-width: 86vw;
            box-shadow: 12px 0 35px rgba(0, 0, 0, 0.2);
            transform: translateX(-100%);
          }
          .nexaflow-sidebar.mobile-open {
            transform: translateX(0);
          }
          .nexaflow-sidebar-mobile-toggle {
            display: flex;
          }
          .nexaflow-sidebar-backdrop {
            display: block;
          }
          .nexaflow-sidebar-close-btn {
            display: inline-flex;
          }
        }
      `}</style>
    </>
  );
};

export default DashboardSidebar;
